import {
  Vector3,
  BufferGeometry,
  Float32BufferAttribute,
  ShaderMaterial,
  Points,
  AdditiveBlending,
  Quaternion
} from 'three';

import vertex from '../../../shaders/outflow/vertexPolarJet.glsl';
import fragment from '../../../shaders/outflow/fragmentPolarJet.glsl';

import { colorSizeAleat } from '../groupsEntities/galaxy.js';

export function createJets(object, pointCount,attrNeutron, config) {
  const mesh = object.mesh;
  const count = Math.floor(pointCount * 0.7);

  const emitters = [];
  const attrsList = [];

  function genBaseAttrs() {
    const colors = new Array(count);
    const sizes = new Float32Array(count);
    const opacities = new Float32Array(count);
    const randoms = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      const { color, size } = colorSizeAleat(attrNeutron);
      colors[i] = color;
      sizes[i] = size;
      opacities[i] = 1.0;
      randoms[i] = Math.random() * 100;
    }
    return { colors, sizes, opacities, randoms };
  }

  function getDirectionVec(invert) {
    const baseDir = new Vector3(0, invert, 0);
    const extraTilt = new Quaternion().setFromAxisAngle(
      new Vector3(1, 0, 0),
      config.inclinationAngle
    );
    return new Vector3().copy(baseDir).applyQuaternion(extraTilt).normalize();
  }

  function makeEmitter(baseAttrs, invert) {
    const { colors, sizes, opacities, randoms } = baseAttrs;

    const directionVec = getDirectionVec(invert);

    const uMinRadius = object.size;
    const uMaxRadius = object.size * config.lengthFactor;
    const radialRadius = object.size * 0.25;

    const coords = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      const t = uMinRadius + Math.random() * (uMaxRadius - uMinRadius);

      const mainX = directionVec.x * t;
      const mainY = directionVec.y * t;
      const mainZ = directionVec.z * t;

      let perp = new Vector3();
      if (Math.abs(directionVec.x) < 0.9) {
        perp.crossVectors(directionVec, new Vector3(1, 0, 0));
      } else {
        perp.crossVectors(directionVec, new Vector3(0, 1, 0));
      }
      perp.normalize();
      const theta = Math.random() * Math.PI * 2;
      perp.applyAxisAngle(directionVec, theta);
      perp.multiplyScalar(Math.random() * radialRadius);

      coords[3 * i + 0] = mainX + perp.x;
      coords[3 * i + 1] = mainY + perp.y;
      coords[3 * i + 2] = mainZ + perp.z;
    }

    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(coords, 3));

    const colorArray = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const c = colors[i];
      colorArray[3 * i + 0] = c.r;
      colorArray[3 * i + 1] = c.g;
      colorArray[3 * i + 2] = c.b;
    }
    geo.setAttribute('color', new Float32BufferAttribute(colorArray, 3));
    geo.setAttribute('sizes', new Float32BufferAttribute(sizes, 1));
    geo.setAttribute('opacity', new Float32BufferAttribute(opacities, 1));
    geo.setAttribute('randomValue', new Float32BufferAttribute(randoms, 1));

    const material = new ShaderMaterial({
      uniforms: {
        uGlobalOpacity: { value: 0.5*config.opacityGlob },
        uMinRadius:     { value: uMinRadius },
        uMaxRadius:     { value: uMaxRadius },
        uMinSize:       { value: config.minSize },
        uMinOpacity:    { value: config.minOpacity },
      },
      vertexShader: vertex,
      fragmentShader: fragment,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      depthTest: true,
    });

    const points = new Points(geo, material);
    mesh.add(points);
    return points;
  }

  const baseAttrsA = genBaseAttrs();
  const baseAttrsB = genBaseAttrs();
  attrsList.push(baseAttrsA, baseAttrsB);

  emitters.push(makeEmitter(baseAttrsA, +1));
  emitters.push(makeEmitter(baseAttrsB, -1));

  return {
    emitters,
    mesh,
    object,
    attrsList,
    config,
  };
}

export function updateJets(jetObj) {
  const { emitters, object, attrsList, config } = jetObj;
  const {
    speedFactor,
    lengthFactor,
    minSize,
    minOpacity,
    rotationStrength,
    rotationDecay,
    collimationStrength,
    collimationDecay,
    respawnProb = 0.01,
  } = config;

  const uMaxRadius = object.size * lengthFactor;
  const gasSphereRadius = object.size * 0.25;

  emitters.forEach((points, idx) => {
    const baseAttrs = attrsList[idx];
    const posAttr = points.geometry.attributes.position;
    const xs = posAttr.array;
    const count = xs.length / 3;

    const invert = idx === 0 ? +1 : -1;
    const directionVec = getDirectionVec(invert);

    const sizesArr = points.geometry.attributes.sizes.array;
    const opacArr  = points.geometry.attributes.opacity.array;

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;

      xs[i3 + 0] += directionVec.x * speedFactor;
      xs[i3 + 1] += directionVec.y * speedFactor;
      xs[i3 + 2] += directionVec.z * speedFactor;

      if (Math.random() < 0.009) {
        xs[i3 + 0] += (Math.random() * 2 - 1) * gasSphereRadius * 4;
        xs[i3 + 1] += (Math.random() * 2 - 1) * gasSphereRadius * 4;
        xs[i3 + 2] += (Math.random() * 2 - 1) * gasSphereRadius * 4;
      }

      const px = xs[i3 + 0],
            py = xs[i3 + 1],
            pz = xs[i3 + 2];
      let dLocal = Math.sqrt(px * px + py * py + pz * pz);
      const distFactor = Math.max(dLocal, 1.0);

      const rotVel = rotationStrength * (1.0 / Math.pow(distFactor, rotationDecay)) * speedFactor;
      const posLocal = new Vector3(px, py, pz);
      const perp = new Vector3().copy(directionVec).cross(posLocal).normalize();
      xs[i3 + 0] += perp.x * rotVel;
      xs[i3 + 1] += perp.y * rotVel;
      xs[i3 + 2] += perp.z * rotVel;

      const projLen = posLocal.dot(directionVec);
      const projPoint = new Vector3().copy(directionVec).multiplyScalar(projLen);
      const offset = new Vector3().copy(projPoint).sub(posLocal);
      const colVel = collimationStrength * (1.0 / Math.pow(distFactor, collimationDecay)) * speedFactor;
      xs[i3 + 0] += offset.x * colVel;
      xs[i3 + 1] += offset.y * colVel;
      xs[i3 + 2] += offset.z * colVel;


      if (dLocal > uMaxRadius && Math.random() < respawnProb) {
        xs[i3 + 0] = (Math.random() * 1.5 - 0.75) * gasSphereRadius;
        xs[i3 + 1] = (Math.random() * 1.5 - 0.75) * gasSphereRadius;
        xs[i3 + 2] = (Math.random() * 1.5 - 0.75) * gasSphereRadius;
        sizesArr[i] = minSize;
        opacArr[i] = minOpacity;
      } else {
        sizesArr[i] = baseAttrs.sizes[i];
        opacArr[i] = baseAttrs.opacities[i];
      }
    }

    ['position', 'sizes', 'opacity'].forEach(key => {
      points.geometry.attributes[key].needsUpdate = true;
    });
  });

  function getDirectionVec(invert) {
    const baseDir = new Vector3(0, invert, 0);
    const extraTilt = new Quaternion().setFromAxisAngle(
      new Vector3(1, 0, 0),
      config.inclinationAngle
    );
    return new Vector3().copy(baseDir).applyQuaternion(extraTilt).normalize();
  }
}
